import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Role, Route } from '../../enums';
import { common } from '../../i18n/common.en';
import { useAuthStore } from '../../store/auth.store';
import { useOrgStore } from '../../store/org.store';
import { useToastStore } from '../../store/toast.store';
import { shellService } from './shell.service';
import { initials } from './format';
import { Wordmark } from './Brand';

/**
 * Sidebar + topbar frame shared by every private page. Drains the toast queue.
 * The menu follows the work: Apps → Tests → Connectors → My agents → Audit, for everyone. Only Settings is
 * admin-only (the one address set on the server); it sits at the bottom, away from daily work.
 */
export function AppShell() {
  const navigate = useNavigate();
  const principal = useAuthStore((s) => s.principal);
  const clear = useAuthStore((s) => s.clear);
  const org = useOrgStore((s) => s.org);
  const setOrg = useOrgStore((s) => s.setOrg);
  const queue = useToastStore((s) => s.queue);
  const drain = useToastStore((s) => s.drain);
  const toast = useRef<Toast>(null);
  const [open, setOpen] = useState(false);
  const [degradedCount, setDegradedCount] = useState(0);
  const [health, setHealth] = useState<{ ok: boolean; version: string } | null | undefined>(undefined);
  const isAdmin = principal?.role === Role.Admin;

  useEffect(() => {
    if (!org) shellService.loadOrg().then(setOrg).catch(() => undefined);
    shellService.loadDegradedCount().then(setDegradedCount).catch(() => undefined);
    const check = () => shellService.health().then(setHealth).catch(() => setHealth(null));
    check();
    const id = window.setInterval(check, 30_000);
    return () => window.clearInterval(id);
  }, [org, setOrg, isAdmin]);

  useEffect(() => {
    if (queue.length && toast.current) {
      toast.current.show(drain());
    }
  }, [queue, drain]);

  const signOut = async () => {
    await shellService.logout().catch(() => undefined);
    clear();
    navigate(Route.Login);
  };

  return (
    <div className="mcper-shell">
      <Toast ref={toast} position="bottom-right" />
      {open && <div className="mcper-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />}
      <aside className={`mcper-sidebar${open ? ' open' : ''}`}>
        <div className="flex flex-column px-2" style={{ gap: 4 }}>
          <Wordmark tone="dark" title={common.app.name} />
          <span className="mcper-brand-sub">{common.app.tagline}</span>
        </div>

        <nav className="mcper-nav" onClick={() => setOpen(false)}>
          <NavLink to={Route.Apps} end>
            <i className="pi pi-th-large" /> {common.nav.apps}
          </NavLink>
          <NavLink to={Route.Tests}>
            <i className="pi pi-check-square" /> {common.nav.tests}
            {degradedCount > 0 && <span className="mcper-nav-badge warn">{degradedCount}</span>}
          </NavLink>
          <NavLink to={Route.Connectors}>
            <i className="pi pi-share-alt" /> {common.nav.connectors}
          </NavLink>
          <NavLink to={Route.MyAgents}>
            <i className="pi pi-link" /> {common.nav.myAgents}
          </NavLink>
          <NavLink to={Route.Audit}>
            <i className="pi pi-list" /> {common.nav.audit}
          </NavLink>
        </nav>

        <div className="mcper-grow" />

        {isAdmin && (
          <nav className="mcper-nav" onClick={() => setOpen(false)}>
            <NavLink to={Route.Settings}>
              <i className="pi pi-cog" /> {common.nav.settings}
            </NavLink>
          </nav>
        )}

        <div className="mcper-server-chip">
          <span className="flex align-items-center gap-2">
            <span className="mcper-pulse" style={health === null ? { background: 'var(--mcper-bad)', boxShadow: 'none' } : undefined} />
            {health === undefined ? common.nav.serverChecking : health === null ? common.nav.serverUnreachable : common.nav.serverHealthy(health.version)}
          </span>
          <span className="mcper-mono" style={{ color: '#6e838b', fontSize: 11.5 }}>
            {window.location.host}
          </span>
        </div>

        <div className="mcper-user">
          <div className="mcper-avatar">{initials(principal?.displayName ?? principal?.email ?? '?')}</div>
          <div className="flex flex-column mcper-grow" style={{ lineHeight: 1.2 }}>
            <span style={{ fontSize: 14, fontWeight: 500 }}>{principal?.displayName ?? principal?.email}</span>
            <span style={{ fontSize: 12, color: '#6e838b' }}>{principal?.role}</span>
          </div>
          <Button icon="pi pi-sign-out" text rounded aria-label={common.nav.signOut} onClick={signOut} style={{ color: '#9fb2b9' }} />
        </div>
      </aside>

      <div className="mcper-main">
        <header className="mcper-topbar">
          <Button icon="pi pi-bars" text className="mcper-menu-btn" aria-label={common.nav.menu} onClick={() => setOpen((o) => !o)} />
          <span className="mcper-grow" />
          <Button icon="pi pi-plus" label={common.topbar.attachApp} onClick={() => navigate(Route.AttachApp)} />
        </header>
        <main className="mcper-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
